import React, { createContext, useContext, useMemo, useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import { setSecureCookie } from '../../utils/cookies';

const TeamsContext = createContext();

function TeamsContextProvider({ children }) {
    // Initialize teams state from cookies or default to empty array
    const [teams, setTeams] = useState(() => {
        const savedTeams = Cookies.get('teams');
        
        // If no teams in cookies, check for legacy team_uuid and create a fallback team
        if (!savedTeams) {
            const legacyTeamUuid = Cookies.get('team_uuid');
            const legacyCredits = Cookies.get('credits');
            
            if (legacyTeamUuid) {
                const fallbackTeam = {
                    uuid: legacyTeamUuid,
                    name: "My Team",
                    credits: legacyCredits ? parseFloat(legacyCredits) : 0,
                    role: "Admin"
                };
                return [fallbackTeam];
            }
        }
        
        return savedTeams ? JSON.parse(savedTeams) : [];
    });

    // Initialize current team from cookies or default to null
    const [currentTeam, setCurrentTeam] = useState(() => {
        const savedCurrentTeam = Cookies.get('currentTeam');
        
        // If no current team in cookies, check for legacy team_uuid and create fallback
        if (!savedCurrentTeam) {
            const legacyTeamUuid = Cookies.get('team_uuid');
            const legacyCredits = Cookies.get('credits');
            
            if (legacyTeamUuid) {
                const fallbackTeam = {
                    uuid: legacyTeamUuid,
                    name: "My Team", 
                    credits: legacyCredits ? parseFloat(legacyCredits) : 0,
                    role: "Admin"
                };
                return fallbackTeam;
            }
        }
        
        return savedCurrentTeam ? JSON.parse(savedCurrentTeam) : null;
    });

    // Update cookies whenever teams change
    useEffect(() => {
        if (teams.length > 0) {
            setSecureCookie('teams', JSON.stringify(teams), { maxAge: 7 * 24 * 60 * 60 }); // Expires in 7 days
        }
    }, [teams]);

    // Update cookies whenever current team changes
    useEffect(() => {
        if (currentTeam) {
            setSecureCookie('currentTeam', JSON.stringify(currentTeam), { maxAge: 7 * 24 * 60 * 60 }); // Expires in 7 days
            // Also update the legacy team_uuid cookie for backward compatibility
            setSecureCookie('team_uuid', currentTeam.uuid, { maxAge: 7 * 24 * 60 * 60 });
        }
    }, [currentTeam]);

    // Sync teams and current team if we have teams but no current team
    useEffect(() => {
        if (teams.length > 0 && !currentTeam) {
            setCurrentTeam(teams[0]);
        }
    }, [teams, currentTeam]);

    // Function to switch to a different team
    const switchToTeam = (teamUuid) => {
        const team = teams.find(t => t.uuid === teamUuid);
        if (team) {
            setCurrentTeam(team);
            return team;
        }
        return null;
    };

    // Function to update teams list (used after login/refresh)
    const updateTeams = (newTeams, defaultTeamUuid = null) => {
        setTeams(newTeams);
        
        // Set current team to the specified default or first team
        if (newTeams.length > 0) {
            const defaultTeam = defaultTeamUuid 
                ? newTeams.find(t => t.uuid === defaultTeamUuid)
                : newTeams[0];
            if (defaultTeam) {
                setCurrentTeam(defaultTeam);
            }
        }
    };

    // Function to update a specific team in the teams list
    const updateTeamInList = (updatedTeam) => {
        setTeams(prevTeams => {
            const newTeams = prevTeams.map(team => 
                team.uuid === updatedTeam.uuid ? updatedTeam : team
            );
            
            // Save updated teams to cookies
            Cookies.set('teams', JSON.stringify(newTeams), { expires: 7, sameSite: 'strict' });
            return newTeams;
        });
        
        // Update current team if it matches the updated team
        if (currentTeam && currentTeam.uuid === updatedTeam.uuid) {
            setCurrentTeam(updatedTeam);
            setSecureCookie('currentTeam', JSON.stringify(updatedTeam));
        }
    };

    // Function to clear all team data (used on logout)
    const clearTeamData = () => {
        setTeams([]);
        setCurrentTeam(null);
        Cookies.remove('teams');
        Cookies.remove('currentTeam');
        Cookies.remove('team_uuid');
    };

    const contextValue = useMemo(() => ({
        teams,
        currentTeam,
        setTeams,
        setCurrentTeam,
        switchToTeam,
        updateTeams,
        updateTeamInList,
        clearTeamData
    }), [teams, currentTeam]);

    return (
        <TeamsContext.Provider value={contextValue}>
            {children}
        </TeamsContext.Provider>
    );
}

export function useTeamsContext() {
    return useContext(TeamsContext);
}

export { TeamsContext };
export default TeamsContextProvider;